import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateInvoiceInZohoResponse, getMonthYearFromBillingPeriod, InvoiceData } from "@/shared/types/invoiceTypes";
import { PaginatedResponse } from '@/shared/types/responseTypes';

// Get Region Invoice Data Query
export const useRegionInvoiceData = (regionCode: string | undefined, billingPeriod: string | undefined) => {
  const parsedBillingPeriod = getMonthYearFromBillingPeriod(billingPeriod);

  return useQuery({
    queryKey: ['region-usage-invoices', regionCode, parsedBillingPeriod],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<InvoiceData>>(
        `/enterprise/v1/invoices`,
        { params: { regionCode, month: parsedBillingPeriod } }
      );
      return response.data.results || [];
    },
    enabled: !!regionCode && !!parsedBillingPeriod
  });
};

// Create Invoice in Zoho
export const useCreateInvoiceInZoho = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, yearMonth }: { organizationId: string, yearMonth: string }) => {
      const parsedBillingPeriod = getMonthYearFromBillingPeriod(yearMonth);
      if (!parsedBillingPeriod) {
        throw new Error('Invalid billing period');
      }
      const response = await apiClient.post<CreateInvoiceInZohoResponse>(
        `/enterprise/v1/invoices`,
        { params: { organizationId, month: parsedBillingPeriod } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['region-usage-invoices'] });
    },
    onError: (error) => {
      return error;
    }
  });
};